import React, { useCallback, useEffect, useState } from 'react';
// eslint-disable-next-line yola/import/no-extraneous-dependencies
import {
  useFloating,
  useInteractions,
  useHover,
  useFocus,
  useClick,
  useDismiss,
  offset,
  autoUpdate,
  safePolygon,
  shift,
  flip,
} from '@floating-ui/react';
import { PLACEMENTS, MODES, MAP_PLACEMENTS_TO_FLOATING_UI_PLACEMENTS } from './constants';
import DropdownContext from './context';
import { DropdownContent, DropdownTrigger } from './components';
import noop from '../../../utils/noop';
const HIDE_EVENT_NAME = 'ws-dropdown:hide';

const hide = () => {
  document.dispatchEvent(new Event(HIDE_EVENT_NAME));
};

const Dropdown = ({
  children,
  placement = PLACEMENTS.topCenter,
  disabled = false,
  mode = MODES.popover,
  isOpen = null,
  width = undefined,
  portalRootNode = undefined,
  onClick = noop,
  onHide = noop,
  onShow = noop,
  zIndex = 999,
  offsetMainAxis = 10,
  offsetCrossAxis = undefined,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isVisible,
    placement: MAP_PLACEMENTS_TO_FLOATING_UI_PLACEMENTS[placement],
    middleware: [
      offset({
        mainAxis: offsetMainAxis,
        crossAxis: offsetCrossAxis,
      }),
      flip({
        fallbackAxisSideDirection: 'start',
        crossAxis: false,
      }),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
    onOpenChange: (isShowed) => {
      if (isShowed) {
        onShow();
      } else {
        onHide();
      }
      setIsVisible(isShowed);
    },
  });

  const hover = useHover(context, {
    enabled: mode === MODES.tooltip && !disabled,
    move: true,
    handleClose: safePolygon(),
  });
  const focus = useFocus(context, { enabled: mode === MODES.tooltip && !disabled });
  const click = useClick(context, {
    enabled: mode === MODES.popover && !disabled && isOpen === null,
  });
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, click, dismiss]);

  const hideDropdown = useCallback(() => {
    if (disabled || !isVisible) {
      return;
    }

    setIsVisible(false);
  }, [disabled, isVisible]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const referenceElement = refs.reference.current;
      const floatingElement = refs.floating.current;
      if (
        floatingElement &&
        referenceElement &&
        !floatingElement.contains(event.target) &&
        !referenceElement.contains(event.target)
      ) {
        hideDropdown();
        onHide();
      }
    };
    window.addEventListener('mousedown', handleClickOutside, false);
    window.addEventListener(HIDE_EVENT_NAME, hideDropdown, false);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside, false);
      window.removeEventListener(HIDE_EVENT_NAME, hideDropdown, false);
    };
  }, [hideDropdown, onHide, refs]);

  return (
    <DropdownContext.Provider
      value={{
        isVisible: isOpen === null ? isVisible : isOpen,
        refs,
        getReferenceProps: (userProps) => getReferenceProps({ ...userProps, onClick }),
        width,
        portalRootNode,
        floatingStyles,
        zIndex,
        getFloatingProps,
      }}
    >
      <div className="ws-ui-dropdown">{children}</div>
    </DropdownContext.Provider>
  );
};

Dropdown.hide = hide;

export { Dropdown, DropdownTrigger, DropdownContent };
